import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchDashboard } from '../store/slices/dashboardSlice';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend
} from 'chart.js';
import {
  Users, Briefcase, AlertTriangle, TrendingUp,
  ChevronRight, Clock, Bell, BarChart2, RefreshCw,
  UserCheck, UserX, XCircle
} from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function KPICard({ label, value, icon: Icon, color, trend, suffix = '' }) {
  return (
    <div className={`kpi-card kpi-card-${color}`}>
      <div className="kpi-content">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value">{value !== undefined ? `${value}${suffix}` : '—'}</div>
        {trend != null && (
          <div className={`kpi-trend ${trend >= 0 ? 'up' : 'down'}`}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% ce mois
          </div>
        )}
      </div>
      <div className={`kpi-icon ${color}`}><Icon size={24} /></div>
    </div>
  );
}

function EcheanceRow({ item }) {
  const jours = Math.round((new Date(item.date_fin) - new Date()) / 86400000);
  const urgence = jours <= 0 ? 'rouge' : jours <= 15 ? 'rouge' : jours <= 30 ? 'orange' : 'bleu';
  const navigate = useNavigate();
  return (
    <tr
      className={`row-urgence-${urgence}`}
      onClick={() => navigate(`/coachings/${item.id}`)}
      style={{ cursor: 'pointer' }}
    >
      <td><strong>{item.client__prenom_cli} {item.client__nom_cli}</strong></td>
      <td style={{ color: '#6B7280' }}>{item.client__telephone_principal}</td>
      <td style={{ textTransform: 'capitalize' }}>{item.domaine?.replace(/_/g, ' ')}</td>
      <td>{new Date(item.date_fin).toLocaleDateString('fr-FR')}</td>
      <td>
        <span className={`badge badge-${urgence}`}>
          {jours <= 0 ? 'Échu' : `J-${jours}`}
        </span>
      </td>
    </tr>
  );
}

const CANAL_ICON = { SMS: '💬', Email: '✉️', WhatsApp: '📱' };

export default function DashboardPage() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { kpis, echeances, evolution, notifs, loading } = useSelector(s => s.dashboard);

  useEffect(() => { dispatch(fetchDashboard()); }, [dispatch]);
  useAutoRefresh(fetchDashboard, 30000);

  const chartEvolution = {
    labels: evolution.map(e => e.mois),
    datasets: [
      {
        label: 'Nouveaux clients',
        data: evolution.map(e => e.clients),
        borderColor: '#2E75B6', backgroundColor: 'rgba(46,117,182,0.12)',
        tension: 0.4, fill: true, pointRadius: 5, pointHoverRadius: 7, borderWidth: 2.5,
      },
      {
        label: 'Coachings signés',
        data: evolution.map(e => e.coachings),
        borderColor: '#1E7145', backgroundColor: 'rgba(30,113,69,0.10)',
        tension: 0.4, fill: true, pointRadius: 5, pointHoverRadius: 7, borderWidth: 2.5,
      },
      {
        label: 'Renouvellements',
        data: evolution.map(e => e.renouvellements || 0),
        borderColor: '#C00000', backgroundColor: 'rgba(192,0,0,0.10)',
        tension: 0.4, fill: true, pointRadius: 5, pointHoverRadius: 7, borderWidth: 2.5,
        borderDash: [5, 3],
      },
    ],
  };

  const optionsLine = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 16, font: { size: 12 }, usePointStyle: true } },
      tooltip: { backgroundColor: '#1F2937', padding: 10, cornerRadius: 8 },
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { font: { size: 12 } } },
      x: { grid: { display: false }, ticks: { font: { size: 12 } } },
    },
  };

  if (loading && !kpis.clients_actifs) return (
    <div className="spinner">
      <div style={{ textAlign: 'center' }}>
        <div className="spinner-ring" />
        Chargement du tableau de bord…
      </div>
    </div>
  );

  return (
    <div className="dashboard">

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <KPICard label="Total clients"           value={kpis.clients_total}            icon={Users}        color="bleu" />
        <KPICard label="Clients actifs"          value={kpis.clients_actifs}           icon={UserCheck}    color="vert" />
        <KPICard label="Clients inactifs"        value={kpis.clients_inactifs}         icon={UserX}        color="orange" />
        <KPICard label="CA du mois (FCFA)"       value={parseInt(kpis.ca_mois || 0).toLocaleString('fr-FR')} icon={TrendingUp} color="bleu" />
        <KPICard label="Coachings en cours"      value={kpis.coachings_en_cours}       icon={Briefcase}    color="vert" />
        <KPICard label="Coachings expirés"       value={kpis.coachings_expires}        icon={AlertTriangle} color="rouge" />
        <KPICard label="Coachings renouvelés"    value={kpis.coachings_renouveles}     icon={RefreshCw}    color="vert" />
        <KPICard label="Coachings non renouvelés" value={kpis.coachings_non_renouveles} icon={XCircle}     color="rouge" />
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div>
            <div className="dash-section-title">
              <BarChart2 size={16} /> Évolution mensuelle
            </div>
            <div className="dash-section-sub">
              Clients, coachings et renouvellements — Janvier {new Date().getFullYear()} à aujourd'hui
            </div>
          </div>
        </div>
        {evolution.length > 0
          ? <div style={{ position: 'relative', height: 300 }}><Line data={chartEvolution} options={optionsLine} /></div>
          : <div className="empty-state"><p>Pas encore de données</p></div>}
      </div>

      <div className="table-container" style={{ marginBottom: 20 }}>
        <div className="table-header">
          <div>
            <div className="dash-section-title">
              <Clock size={16} /> Prochaines échéances
            </div>
            <div className="dash-section-sub">Coachings arrivant à terme dans les 30 jours</div>
          </div>
          <button className="header-btn btn-secondary btn-sm" onClick={() => navigate('/notifications')}>
            Voir tout <ChevronRight size={14} />
          </button>
        </div>

        {echeances.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Téléphone</th>
                <th>Domaine</th>
                <th>Date fin</th>
                <th>Urgence</th>
              </tr>
            </thead>
            <tbody>
              {echeances.map(e => <EcheanceRow key={e.id} item={e} />)}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>Aucune échéance dans les 30 prochains jours</p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="dash-section-title">
            <Bell size={16} /> Notifications récentes
          </div>
          <button className="header-btn btn-secondary btn-sm" onClick={() => navigate('/notifications')}>
            Voir tout <ChevronRight size={14} />
          </button>
        </div>

        {notifs.length > 0 ? (
          <div className="notif-list">
            {notifs.map((n, i) => {
              const statutColor = n.statut === 'envoye' ? 'vert' : n.statut === 'echec' ? 'rouge' : 'orange';
              return (
                <div key={i} className={`notif-item notif-accent-${statutColor}`}>
                  <div className="notif-canal-pill">
                    {CANAL_ICON[n.canal] || '📩'}&nbsp;{n.canal}
                  </div>
                  <div className="notif-body">
                    <span className="notif-type">{n.type_not?.replace(/_/g, ' ')}</span>
                    {n.destinataire_nom && (
                      <span className="notif-dest">— {n.destinataire_nom}</span>
                    )}
                  </div>
                  <div className="notif-meta">
                    <span className={`badge badge-${statutColor}`}>{n.statut}</span>
                    <span className="notif-date">
                      {new Date(n.date_heure_envoi).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <p>Aucune notification récente</p>
          </div>
        )}
      </div>

    </div>
  );
}
